const POINTS = {
    client: { x: 160, y: 215 },
    server: { x: 570, y: 150 },
    amo: { x: 607, y: 298 },
    store: { x: 797, y: 298 },
};

const nodeClass = (id, currentStep) => {
    const sender = currentStep.from === id;
    const recipient = currentStep.to === id && !currentStep.dropped;
    const dropped = currentStep.to === id && currentStep.dropped;
    return `lab1-node${sender ? ' is-sender' : ''}${recipient ? ' is-recipient' : ''}${dropped ? ' is-dropped-target' : ''}`;
};

function ActiveRoute({ currentStep }) {
    const start = POINTS[currentStep.from];
    const end = POINTS[currentStep.to];
    if (!start || !end) return null;

    if (currentStep.from === currentStep.to) {
        return (
            <circle className="lab-flow-loop" cx={start.x} cy={start.y} r="76" aria-hidden="true">
                <animate attributeName="r" values="68;96" begin="0s" dur="1.25s" repeatCount="indefinite" />
                <animate attributeName="opacity" values="0.8;0" begin="0s" dur="1.25s" repeatCount="indefinite" />
            </circle>
        );
    }

    const destination = currentStep.dropped
        ? { x: start.x + ((end.x - start.x) * 0.68), y: start.y + ((end.y - start.y) * 0.68) }
        : end;

    return (
        <g className={`lab-flow-active${currentStep.dropped ? ' is-dropped' : ''}`} key={currentStep.name} aria-hidden="true">
            <line x1={start.x} y1={start.y} x2={destination.x} y2={destination.y} markerEnd={currentStep.dropped ? undefined : 'url(#lab1-arrow)'} />
            <circle className="lab-flow-packet" cx={start.x} cy={start.y} r="8">
                <animate attributeName="cx" from={start.x} to={destination.x} begin="0s" dur="1.25s" calcMode="linear" repeatCount="indefinite" />
                <animate attributeName="cy" from={start.y} to={destination.y} begin="0s" dur="1.25s" calcMode="linear" repeatCount="indefinite" />
                {currentStep.dropped && <animate attributeName="opacity" values="1;1;0" keyTimes="0;0.7;1" begin="0s" dur="1.25s" repeatCount="indefinite" />}
            </circle>
            {currentStep.dropped && (
                <g className="lab-flow-drop-marker" transform={`translate(${destination.x} ${destination.y})`}>
                    <circle r="15" /><path d="M -5 -5 L 5 5 M 5 -5 L -5 5" />
                </g>
            )}
        </g>
    );
}

export default function LabOneDiagram({ currentStep }) {
    return (
        <svg className="lab-flow-diagram lab1-architecture" viewBox="0 0 1000 430" role="img" aria-label={`Lab 1 nested architecture: ${currentStep.message}`}>
            <defs>
                <pattern id="lab1-grid" width="24" height="24" patternUnits="userSpaceOnUse">
                    <path d="M 24 0 L 0 0 0 24" fill="none" stroke="currentColor" strokeWidth="0.5" />
                </pattern>
                <marker id="lab1-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                    <path className="lab-flow-arrow-head" d="M 0 0 L 10 5 L 0 10 z" />
                </marker>
            </defs>
            <rect className="lab-flow-grid" width="1000" height="430" fill="url(#lab1-grid)" />

            <rect className="lab1-server-boundary" x="430" y="45" width="510" height="340" rx="18" />
            <text className="lab1-boundary-label" x="455" y="76">SERVER PROCESS</text>
            <text className="lab1-boundary-meta" x="915" y="76" textAnchor="end">one request at a time</text>

            <rect className={`lab1-amo-boundary${currentStep.from === 'amo' || currentStep.to === 'amo' ? ' is-active' : ''}`} x="500" y="230" width="400" height="125" rx="12" />
            <text className="lab1-amo-label" x="520" y="254">AMOApplication wrapper</text>

            <line className="lab-flow-base" x1={POINTS.client.x} y1={POINTS.client.y} x2={POINTS.server.x} y2={POINTS.server.y} />
            <line className="lab-flow-base" x1={POINTS.server.x} y1={POINTS.server.y} x2={POINTS.amo.x} y2={POINTS.amo.y} />
            <line className="lab-flow-base" x1={POINTS.amo.x} y1={POINTS.amo.y} x2={POINTS.store.x} y2={POINTS.store.y} />
            <text className="lab1-route-label" x="325" y="170">unreliable request / reply</text>
            <text className="lab1-route-label" x="585" y="202">delegate</text>
            <text className="lab1-route-label" x="700" y="288">execute on cache miss</text>

            <ActiveRoute currentStep={currentStep} />

            <g className={nodeClass('client', currentStep)} transform="translate(55 145)">
                <rect width="210" height="140" rx="14" />
                <text className="lab1-node-kicker" x="20" y="28">CLIENT PROCESS</text>
                <text className="lab1-node-title" x="20" y="61">SimpleClient</text>
                <text className="lab1-node-copy" x="20" y="88">sequence #42 · one pending RPC</text>
                <text className="lab1-node-copy" x="20" y="109">ClientTimer · resend / discard</text>
            </g>

            <g className={nodeClass('server', currentStep)} transform="translate(475 100)">
                <rect width="190" height="100" rx="11" />
                <text className="lab1-node-kicker" x="18" y="26">RPC ENDPOINT</text>
                <text className="lab1-node-title" x="18" y="56">SimpleServer</text>
                <text className="lab1-node-copy" x="18" y="78">Request → Reply</text>
            </g>

            <g className={nodeClass('amo', currentStep)} transform="translate(530 270)">
                <rect width="155" height="56" rx="8" />
                <text className="lab1-node-title is-small" x="14" y="24">Deduplication</text>
                <text className="lab1-node-copy" x="14" y="42">client → last result</text>
            </g>

            <g className={nodeClass('store', currentStep)} transform="translate(720 270)">
                <rect width="155" height="56" rx="8" />
                <text className="lab1-node-title is-small" x="14" y="24">KVStore</text>
                <text className="lab1-node-copy" x="14" y="42">Get · Put · Append</text>
            </g>

            <g className="lab1-guarantee" transform="translate(55 345)">
                <circle cx="5" cy="5" r="5" />
                <text x="18" y="9">Retries happen at the RPC layer; duplicate suppression happens before application execution.</text>
            </g>
        </svg>
    );
}
